import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Container, Row, Badge, Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ArrowLeft, FileText, Eye, Download } from 'lucide-react';
import { toast } from 'react-toastify';
import Breadcrumbs from '../CommonElements/Breadcrumbs';
import { formatDate, numberFormat } from '../Utils';
import { useUser } from '../Contexts/UserContext';
import { getBillById as getClientBillById } from '../Services/ClientService';
import { getBillById as getTenantBillById } from '../Services/BillService';

const documentViewerStyles = `
    .modal-document-viewer .modal-dialog {
        max-width: 90vw;
        margin: 1.75rem auto;
    }
    .modal-document-viewer .modal-content {
        height: 90vh;
    }
    .modal-document-viewer .modal-body {
        padding: 0;
        overflow: hidden;
    }
    .modal-document-viewer iframe {
        width: 100%;
        height: 100%;
        border: none;
    }
`;

interface BillData {
    bill_id: string;
    property?: { name?: string };
    bill_date: string;
    bill_type: string;
    start_date: string;
    end_date: string;
    meter?: any;
    utility?: { name?: string; si_unit?: string };
    supplier?: { name?: string };
    total_consumption: number | string;
    cost: number | string;
    currency?: { major_symbol?: string };
    status: string;
    notes?: string;
    document?: string;
    month: number;
    days: number;
    year: number;
    comment?: string;
}

export default function ViewBill() {
    const { billId } = useParams<{ billId: string }>();
    const navigate = useNavigate();
    const { type } = useUser();

    const [bill, setBill] = useState<BillData | null>(null);
    const [loading, setLoading] = useState(true);
    const [documentModal, setDocumentModal] = useState(false);
    const [documentUrl, setDocumentUrl] = useState<string>('');

    // Choose which service to use based on user type
    const getBillById = type === 'client' ? getClientBillById : getTenantBillById;

    const fetchBill = async (id: string) => {
        try {
            setLoading(true);
            const billData = await getBillById(id);
            setBill(billData);
        } catch (error: any) {
            toast.error(error.message || 'Failed to fetch bill details');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (billId) {
            fetchBill(billId);
        }
    }, [billId]);

    const handleViewDocument = (url: string) => {
        setDocumentUrl(url);
        setDocumentModal(true);
    };

    const handleDownloadDocument = (url: string, fileName: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        window.open(link.href, '_blank');
        document.body.removeChild(link);
    };

    const getStatusColor = (status: string) => {
        const colorMap: Record<string, string> = {
            'in_review': 'warning',
            'dont_pay': 'danger',
            'pay': 'success',
        };
        return colorMap[status] || 'secondary';
    };

    const getStatusLabel = (status: string) => {
        const labelMap: Record<string, string> = {
            'in_review': 'In Review',
            'dont_pay': "Don't Pay",
            'pay': 'Pay',
        };
        return labelMap[status] || status;
    };

    if (loading) {
        return (
            <Container fluid>
                <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Container>
        );
    }

    if (!bill) {
        return (
            <Container fluid>
                <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                    <h4>Bill not found</h4>
                </div>
            </Container>
        );
    }

    return (
        <>
            <style>{documentViewerStyles}</style>
            <Container fluid>
                <Row>
                    <Col>
                        <div className="d-flex align-items-center mb-3">
                            <Button color="light" className="me-3" onClick={() => navigate(-1)}>
                                <ArrowLeft size={16} />
                            </Button>
                            <Breadcrumbs pageTitle="Bill Details" />
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col lg={8}>
                        <Card>
                            <CardHeader>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">Bill Information</h4>
                                    <Badge color={getStatusColor(bill.status)}>
                                        {getStatusLabel(bill.status)}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md={6}><strong>Property:</strong><p>{bill.property?.name || 'N/A'}</p></Col>
                                    <Col md={6}>
                                        <strong>Meter:</strong>
                                        <p>{bill.meter?.name || ''}</p>
                                        <p>{bill.meter?.site_id || ''}</p>
                                    </Col>
                                    <Col md={6}><strong>Bill Date:</strong><p>{formatDate(bill.bill_date)}</p></Col>
                                    <Col md={6}><strong>Bill Type:</strong><p>{bill.bill_type[0].toUpperCase() + bill.bill_type.slice(1)}</p></Col>
                                    <Col md={6}><strong>Duration:</strong><p>{formatDate(bill.start_date)} - {formatDate(bill.end_date)}</p></Col>
                                    <Col md={6}><strong>Utility:</strong><p>{bill.utility?.name || 'N/A'}</p></Col>
                                    <Col md={6}><strong>Supplier:</strong><p>{bill.supplier?.name || 'N/A'}</p></Col>
                                    <Col md={6}><strong>Total Consumption:</strong><p>{numberFormat(bill.total_consumption, 0, 0)} {bill.utility?.si_unit || ''}</p></Col>
                                    <Col md={6}><strong>Total Cost:</strong><p>£{numberFormat(bill.cost)} {bill.currency?.major_symbol || ''}</p></Col>
                                    <Col md={6}><strong>Days:</strong><p>{bill.days}</p></Col>
                                    <Col md={6}><strong>Month/Year:</strong><p>{bill.month}/{bill.year}</p></Col>

                                    {bill.notes && <Col md={12}><strong>Notes:</strong><p>{bill.notes}</p></Col>}
                                    {bill.comment && <Col md={12}><strong>Notes:</strong><p>{bill.comment}</p></Col>}
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card>
                            <CardHeader><h5 className="mb-0">Bill Document</h5></CardHeader>
                            <CardBody>
                                {bill.document ? (
                                    <div className="d-flex flex-column gap-2">
                                        <Button color="primary" outline onClick={() => handleViewDocument(bill.document!)}><Eye size={16} className="me-2" />View Document</Button>
                                        <Button color="success" outline onClick={() => handleDownloadDocument(bill.document!, `bill_${bill.bill_id}.pdf`)}><Download size={16} className="me-2" />Download Document</Button>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted">
                                        <FileText size={48} className="mb-2" />
                                        <p>No document attached</p>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal isOpen={documentModal} toggle={() => setDocumentModal(false)} size="xl" className="modal-document-viewer">
                <ModalHeader toggle={() => setDocumentModal(false)}>Bill Document</ModalHeader>
                <ModalBody>
                    {documentUrl && <iframe src={documentUrl} title="Bill Document" />}
                </ModalBody>
            </Modal>
        </>
    );
}
